import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UpsertMedicalHistoryInput } from "@shared/validation/medicalHistory.schema";
import { apiClient, ApiError } from "./client";

export interface MedicalHistoryData {
  id: string;
  childId: string;
  primaryPhysicianName: string | null;
  primaryPhysicianPhone: string | null;
  dentistName: string | null;
  dentistPhone: string | null;
  bloodType: string | null;
  conditions: string | null;
  pastIllnesses: string | null;
  hospitalizations: string | null;
  insuranceNotes: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const medicalHistoryKey = (childId: string) =>
  ["children", childId, "medicalHistory"] as const;

export function useMedicalHistory(childId: string | undefined) {
  return useQuery<MedicalHistoryData | null, ApiError>({
    queryKey: childId
      ? medicalHistoryKey(childId)
      : ["children", "__none__", "medicalHistory"],
    queryFn: () => apiClient<MedicalHistoryData | null>(`/api/medical-history/${childId}`),
    enabled: Boolean(childId),
  });
}

export function useSaveMedicalHistory(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<MedicalHistoryData, ApiError, UpsertMedicalHistoryInput>({
    mutationFn: (vars) =>
      apiClient<MedicalHistoryData>(`/api/medical-history/${childId}`, {
        method: "PUT",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: medicalHistoryKey(childId) });
    },
  });
}
